import { datadogRum } from "@datadog/browser-rum";
import Cookies from "js-cookie";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserCtx } from "../contexts/UserContext";

interface IpInfo {
  ip?: string;
  country?: string;
}

export function useUserContext(): void {
  const user = useContext(UserCtx);

  const [ipInfo, setIpInfo] = useState<IpInfo>({});
  const [ipFetchError, setIpFetchError] = useState<unknown>(null);
  const hasFetchedIpInfoRef = useRef(false);

  const anonymousId = useMemo(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const anonIdParam = urlParams.get("yzsa");
      if (anonIdParam) {
        return anonIdParam;
      }
    } catch {}
    try {
      const cookieVal = Cookies.get("yzsa");
      if (cookieVal) {
        return cookieVal;
      }
    } catch {}
    return undefined;
  }, []);

  useEffect(() => {
    if (hasFetchedIpInfoRef.current) return;
    hasFetchedIpInfoRef.current = true;

    let isActive = true;
    async function fetchIpInfo() {
      try {
        const res = await fetch("https://ipinfo.io/json", {
          credentials: "omit",
        });
        if (!res.ok) throw new Error(`ipinfo.io error: ${res.status}`);
        const data = (await res.json()) as { ip?: string; country?: string };
        if (isActive) {
          setIpInfo({ ip: data?.ip, country: data?.country });
        }
      } catch (err) {
        setIpFetchError(err);
        try {
          // @ts-ignore
          window?.newrelic?.noticeError?.(err);
        } catch {}
        console.error("Failed to fetch IP info for Datadog RUM context", err);
      }
    }
    fetchIpInfo();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    try {
      const isRumReady = !!datadogRum.getInternalContext?.();
      if (!isRumReady) return;

      const isLoggedIn = !!user && !user.isAnonymous;
      const email = isLoggedIn ? user?.email?.trim().toLowerCase() : undefined;
      const userId = isLoggedIn ? user?.uid : undefined;

      const globalContext = {
        email,
        userId,
        anonymousId,
        ipAddress: ipInfo.ip,
        country: ipInfo.country,
      } as Record<string, unknown>;

      datadogRum.setGlobalContext(globalContext);

      if (isLoggedIn && userId) {
        datadogRum.setUser({ id: userId, email });
      } else if (anonymousId) {
        datadogRum.setUser({ id: anonymousId });
      }
    } catch (err) {
      try {
        // @ts-ignore
        window?.newrelic?.noticeError?.(err);
      } catch {}
      console.error("Failed to bind Datadog RUM user context", err);
    }
  }, [
    user?.uid,
    user?.email,
    user?.isAnonymous,
    anonymousId,
    ipInfo.ip,
    ipInfo.country,
    user,
  ]);

  // eslint guard to reference ipFetchError for potential debugging without re-renders
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ = ipFetchError;
}

export default useUserContext;
